// Sidebar Item Component - AgentBheem ERP
// Vue-inspired Modern Dark Theme with Indigo/Purple Accent
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { MenuIcon } from './MenuIcon'
import type { MenuItem } from '@/types/menu'

interface SidebarItemProps {
  item: MenuItem
  isCollapsed: boolean
  depth?: number
}

export function SidebarItem({ item, isCollapsed, depth = 0 }: SidebarItemProps) {
  const location = useLocation()
  const hasChildren = item.children && item.children.length > 0

  // Check if this item or any child is active
  const isActive = item.path === location.pathname
  const hasActiveChild = item.children?.some(
    (child) =>
      child.path === location.pathname ||
      child.children?.some((c) => c.path === location.pathname)
  )

  // Only auto-expand if has active child (start collapsed otherwise)
  const [isExpanded, setIsExpanded] = useState(hasActiveChild || false)

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded)
    }
  }

  // External or custom URL
  if (item.custom_url) {
    return (
      <a
        href={item.custom_url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'group flex items-center gap-3 rounded-xl px-3 py-3',
          'text-slate-400 transition-all duration-200',
          'hover:bg-slate-800/60 hover:text-slate-200',
          depth > 0 && 'ml-3 py-2.5'
        )}
      >
        <div
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
            'bg-slate-800/80',
            'group-hover:bg-indigo-500/20 group-hover:shadow-lg group-hover:shadow-indigo-500/10'
          )}
        >
          <MenuIcon
            name={item.icon || item.name}
            className="h-4 w-4 text-indigo-400"
          />
        </div>
        {!isCollapsed && <span className="truncate text-sm font-medium">{item.name}</span>}
      </a>
    )
  }

  // Has children - expandable section
  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={cn(
            'group flex w-full items-center gap-3 rounded-xl px-3 py-3',
            'text-slate-400 transition-all duration-200',
            'hover:bg-slate-800/60 hover:text-slate-200',
            (isActive || hasActiveChild) && 'text-slate-200 bg-slate-800/60',
            depth > 0 && 'ml-3 py-2.5'
          )}
        >
          <div
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
              'bg-slate-800/80',
              'group-hover:bg-indigo-500/20 group-hover:shadow-lg group-hover:shadow-indigo-500/10',
              (isActive || hasActiveChild) && 'bg-indigo-500/20 shadow-lg shadow-indigo-500/10'
            )}
          >
            <MenuIcon
              name={item.icon || item.name}
              className={cn(
                'h-4 w-4 transition-colors',
                (isActive || hasActiveChild) ? 'text-indigo-400' : 'text-indigo-400'
              )}
            />
          </div>
          {!isCollapsed && (
            <>
              <span className="flex-1 truncate text-left text-sm font-medium">{item.name}</span>
              <svg
                className={cn(
                  'h-4 w-4 transition-all duration-300',
                  'text-slate-500',
                  isExpanded ? 'rotate-90 text-indigo-400' : ''
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>

        {/* Children */}
        {!isCollapsed && isExpanded && (
          <div className="mt-1 space-y-1 ml-6 pl-3 border-l-2 border-slate-700/50">
            {item.children.map((child) => (
              <SidebarItem
                key={child.id}
                item={child}
                isCollapsed={isCollapsed}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Regular menu item with path
  if (item.path) {
    return (
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-xl px-3 py-3',
            'text-sm font-medium transition-all duration-200',
            isActive
              ? 'bg-slate-800/60 text-slate-100'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60',
            depth > 0 && 'ml-3 py-2.5'
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Active indicator - left border */}
            {isActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] bg-gradient-to-b from-indigo-400 to-purple-500 rounded-r-full" />
            )}

            <div
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
                isActive
                  ? 'bg-indigo-500/25 shadow-lg shadow-indigo-500/20'
                  : 'bg-slate-800/80 group-hover:bg-indigo-500/20 group-hover:shadow-lg group-hover:shadow-indigo-500/10'
              )}
            >
              <MenuIcon
                name={item.icon || item.name}
                className="h-4 w-4 text-indigo-400"
              />
            </div>
            {!isCollapsed && <span className="truncate">{item.name}</span>}
          </>
        )}
      </NavLink>
    )
  }

  // Fallback - item without path
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl px-3 py-3',
        'text-slate-500 text-sm',
        depth > 0 && 'ml-3 py-2.5'
      )}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-800/80">
        <MenuIcon name={item.icon || item.name} className="h-4 w-4 text-indigo-400" />
      </div>
      {!isCollapsed && <span className="truncate font-medium">{item.name}</span>}
    </div>
  )
}
